import { api } from './api';
import { formatDateOnly } from './taskDates';

/**
 * Task reminders: the moment, the record of what has already been said, and the
 * notification itself.
 *
 * Delivery is the browser's `Notification`, raised by `ReminderScheduler` from
 * whatever tab has Hive open. That is the whole mechanism and it has one honest
 * limit: nothing fires when no tab has Hive loaded. Firing with the browser shut
 * means Web Push -- a service worker, a manifest, VAPID keys through env and
 * deploy, a subscription table and a dispatcher on the server -- and none of that
 * exists here.
 *
 * `Notification` also needs a secure context. Dev is localhost and production is
 * https, so both qualify and there is no third case to handle.
 */

/** One upcoming reminder, as `GET /api/tasks/reminders` sends it. Hand-kept in
 *  sync with `toReminderResponse` in server/src/lib/task-dto.ts, the same way
 *  `TaskSummary` is -- there is no shared types package. */
export type Reminder = {
  id: string;
  title: string;
  /** A full ISO instant, unlike `dueDate`. */
  remindAt: string;
  dueDate: string | null;
  project: { id: string; name: string };
  parent: { id: string; title: string } | null;
};

export type ReminderWindow = {
  reminders: Reminder[];
  /** The end of the server's window, so the scheduler can aim a refetch at it
   *  without holding a copy of the constant that defines it. */
  horizon: string;
};

export function fetchReminders(signal?: AbortSignal): Promise<ReminderWindow> {
  return api.get<ReminderWindow>('/api/tasks/reminders', signal);
}

/**
 * How long before the moment the first notification goes out.
 *
 * Two notifications rather than one because a reminder you receive at the moment
 * itself is already late for anything that needs starting -- the lead-in is the
 * one that lets you put the phone down and get to the desk.
 */
export const LEAD_MS = 10 * 60 * 1000;

/**
 * How much lead has to be left for the lead-in to still be worth raising.
 *
 * It is not enough to ask whether the lead moment has passed. A hidden tab has
 * its timers clamped -- Chrome drops a long-hidden one to roughly a wakeup a
 * minute -- so the lead moment is routinely *seen* late even though the reminder
 * itself is still nine minutes off. Refusing every late one would mean the
 * lead-in almost never fires in a background tab, which is precisely the tab
 * this whole feature exists for.
 *
 * So the question is about the reminder rather than the lead: raise it while
 * there is still real warning in it, and let the wording say how long is
 * actually left. Under a minute there is nothing left to warn about and the
 * moment itself is about to speak for itself.
 */
export const LEAD_FLOOR_MS = 60 * 1000;

/**
 * How late a missed reminder may be and still be worth raising.
 *
 * The case is a laptop opened at 09:40 with a reminder set for 09:30: that is
 * still actionable and silence would be a bug. A week later it is not, and eleven
 * notifications arriving at once on Monday morning would be worse than none --
 * so anything older is crossed off without a word.
 */
export const CATCH_UP_MS = 60 * 60 * 1000;

/** The two moments one reminder produces. */
export type ReminderPhase = 'lead' | 'due';

export function momentFor(reminder: Reminder, phase: ReminderPhase): number {
  const at = new Date(reminder.remindAt).getTime();
  return phase === 'lead' ? at - LEAD_MS : at;
}

const STORAGE_KEY = 'hive.reminders.fired';

/** Seven days, after which a fired entry is dropped. Anything that old is far
 *  outside the server's window and can never be asked about again, so keeping it
 *  only grows the record. */
const PRUNE_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

type FiredMap = Record<string, string>;

/**
 * What has already been said, kept per browser rather than in a column.
 *
 * Keyed `"<taskId>:<phase>"` and holding the `remindAt` it fired for, so "have I
 * said this?" is one string compare -- and *moving* a reminder re-arms it for
 * free, because the stored instant no longer matches. There is no separate
 * invalidation step to forget.
 *
 * Deliberately not a server column. A stamp on the task would bump `updatedAt`,
 * and the Tasks page sorts by `updatedAt` -- so every notification would silently
 * reshuffle the list behind it. It would also be app-wide, where this is a record
 * of what *this browser* has told *this reader*, which is the actual question.
 *
 * Sharing across a reader's own devices is the thing given up. Two browsers both
 * open at the time will each raise it once.
 *
 * Storage access is wrapped because localStorage throws rather than no-ops when
 * the browser blocks site data -- the same reason lib/projectView.ts wraps it.
 * A read that fails reports nothing fired, which risks a repeat notification
 * rather than a missed one; of the two that is the right way to fail.
 */
function readFired(): FiredMap {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as FiredMap;
  } catch {
    return {};
  }
}

export function hasFired(reminder: Reminder, phase: ReminderPhase): boolean {
  return readFired()[`${reminder.id}:${phase}`] === reminder.remindAt;
}

export function markFired(reminder: Reminder, phase: ReminderPhase): void {
  try {
    const fired = readFired();
    fired[`${reminder.id}:${phase}`] = reminder.remindAt;

    const cutoff = Date.now() - PRUNE_AFTER_MS;
    for (const [key, instant] of Object.entries(fired)) {
      const at = new Date(instant).getTime();
      if (Number.isNaN(at) || at < cutoff) delete fired[key];
    }

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(fired));
  } catch {
    /* Same trade as the read: a repeat notification beats taking the page down. */
  }
}

/**
 * Reminders may have changed in this tab.
 *
 * A bare window event rather than a callback threaded through the forms: the two
 * choke points that can change a reminder are `createTask` and `updateTask` in
 * lib/tasks.ts, and neither should have to know the scheduler exists. The
 * scheduler's other triggers -- focus, visibility, its own timer -- already cover
 * every other way the list can go stale.
 */
const CHANGED_EVENT = 'hive:reminders-changed';

export function remindersChanged(): void {
  window.dispatchEvent(new Event(CHANGED_EVENT));
}

export function onRemindersChanged(listener: () => void): () => void {
  window.addEventListener(CHANGED_EVENT, listener);
  return () => window.removeEventListener(CHANGED_EVENT, listener);
}

export type NotificationPermissionState = NotificationPermission | 'unsupported';

export function notificationPermission(): NotificationPermissionState {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

/** Must be called from a click. Safari refuses a permission request that did not
 *  come from a user gesture, which is why the panel offers a button rather than
 *  asking the moment a time is typed. */
export async function requestNotificationPermission(): Promise<NotificationPermissionState> {
  if (!('Notification' in window)) return 'unsupported';
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

/** A time of day, for the body of a notification that is running late. */
function clockOf(instant: string): string {
  return new Date(instant).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/**
 * Raise one notification.
 *
 * The title is the task, parent-qualified, because a subtask title is routinely
 * written to be read against its parent and "on Mac" on its own says nothing --
 * the same reasoning that puts `parent` on the wire.
 *
 * `tag` is the task and phase, so a reminder re-armed and fired again replaces
 * the old bubble instead of stacking a second one beside it. Clicking focuses
 * the window and opens the task, which is the only useful thing a reminder can
 * offer to do.
 */
export function showReminder(
  reminder: Reminder,
  phase: ReminderPhase,
  options: { late: boolean; remainingMs: number; onOpen: (taskId: string) => void },
): void {
  // Counted from what is actually left rather than from LEAD_MS. A lead-in that
  // the tab only got round to raising forty seconds late would otherwise promise
  // ten minutes and deliver nine -- and the whole value of the warning is that
  // the number in it is true.
  const minutes = Math.max(1, Math.round(options.remainingMs / 60_000));

  const when =
    phase === 'lead'
      ? `In ${minutes} minute${minutes === 1 ? '' : 's'}`
      : options.late
        ? `Was ${clockOf(reminder.remindAt)}`
        : 'Now';

  const body = [
    when,
    reminder.project.name,
    reminder.dueDate ? `Due ${formatDateOnly(reminder.dueDate)}` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');

  const notification = new Notification(
    reminder.parent ? `${reminder.parent.title} · ${reminder.title}` : reminder.title,
    { body, tag: `${reminder.id}:${phase}`, data: { taskId: reminder.id } },
  );

  notification.onclick = () => {
    window.focus();
    notification.close();
    options.onOpen(reminder.id);
  };
}

/**
 * The row badge for a reminder that has not happened yet, or null.
 *
 * Only ahead of now, and only on unfinished work: a bell against a moment that
 * has been, or against a task already ticked off, promises a notification that
 * is never coming. It reads the clock at render rather than holding a timer --
 * the list re-renders often enough, and a badge a few minutes stale is not worth
 * a wakeup a minute across every row on screen.
 */
export function reminderBadge(task: {
  remindAt: string | null;
  status: string;
}): { clock: string; title: string } | null {
  if (!task.remindAt || task.status === 'DONE') return null;
  const at = new Date(task.remindAt);
  const time = at.getTime();
  if (Number.isNaN(time) || time <= Date.now()) return null;

  return {
    clock: at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }),
    title: `Reminder ${at.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })}`,
  };
}
